// Admin template list per the July-2026 mock: one card per template with a
// tinted icon disc, description, version pills, and an edit-draft action;
// a dashed "create new" card closes the list. Entry points into the builder.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  PencilLine,
  Copy,
  Briefcase,
  Landmark,
  Star,
  FileText,
  BookOpen,
  Sparkles,
  Trash2,
} from "lucide-react";
import type { TemplateRecord } from "@agp/shared";
import { useApi } from "@/hooks/useApi";
import { useMe } from "@/hooks/useMe";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// Deterministic icon + tint per template id so a template keeps its identity
// across screens and sessions (same trick as the avatar tones).
const TEMPLATE_ICONS = [
  { icon: Briefcase, classes: "bg-success-tint text-success-on" },
  { icon: Landmark, classes: "bg-purple-tint text-purple-on" },
  { icon: Star, classes: "bg-brand-tint text-brand-on" },
  { icon: FileText, classes: "bg-orange-tint text-orange-on" },
  { icon: BookOpen, classes: "bg-warn-tint text-warn-on" },
  { icon: Sparkles, classes: "bg-danger-tint text-danger-on" },
] as const;

function templateIconFor(templateId: string) {
  let h = 0;
  for (let i = 0; i < templateId.length; i++) h = (h * 31 + templateId.charCodeAt(i)) >>> 0;
  return TEMPLATE_ICONS[h % TEMPLATE_ICONS.length]!;
}

// Only the versions that matter day-to-day: the open draft and the latest
// published one (generation always uses the latest published). Older
// published rows stay in the database for traceability and unfold on demand.
function VersionPills({ versions }: { versions: TemplateRecord[] }) {
  const [showOlder, setShowOlder] = useState(false);
  const draft = versions.find((v) => v.status === "draft");
  const published = versions.filter((v) => v.status === "published"); // newest first
  const older = published.slice(1);
  const shown = [
    ...(draft ? [draft] : []),
    ...published.slice(0, 1),
    ...(showOlder ? older : []),
  ];
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2">
      {shown.map((v) => (
        <Link key={v.rowId} to={`/templates/${v.rowId}`} className="no-underline">
          <Badge variant={v.status === "draft" ? "warn" : "ok"}>
            v{v.version} · {v.status}
          </Badge>
        </Link>
      ))}
      {older.length > 0 && (
        <button
          type="button"
          onClick={() => setShowOlder((s) => !s)}
          className="text-[12px] font-medium text-text-2 hover:text-text"
        >
          {showOlder ? "Hide older" : `+${older.length} older`}
        </button>
      )}
    </div>
  );
}

export function Templates() {
  const api = useApi();
  const me = useMe();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [kindTab, setKindTab] = useState<"resume" | "jd">("resume");
  const [pendingDelete, setPendingDelete] = useState<{ templateId: string; name: string } | null>(
    null,
  );
  const qc = useQueryClient();
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (creating) nameInputRef.current?.focus();
  }, [creating]);

  const listQuery = useQuery({
    queryKey: ["admin-templates"],
    queryFn: () => api.get<{ templates: TemplateRecord[] }>("/api/admin/templates"),
    enabled: me?.role === "admin",
  });

  if (me && me.role !== "admin") {
    return (
      <p className="py-10 text-center text-[14px] text-text-3">
        Template administration needs an admin account.
      </p>
    );
  }

  // Group versions per template, then split by kind for the tab strip.
  const grouped = new Map<string, TemplateRecord[]>();
  for (const t of listQuery.data?.templates ?? []) {
    const list = grouped.get(t.templateId) ?? [];
    list.push(t);
    grouped.set(t.templateId, list);
  }
  const visible = [...grouped.entries()].filter(([, versions]) => {
    const kind = versions[0]!.definition.kind === "jd" ? "jd" : "resume";
    return kind === kindTab;
  });

  const deleteTemplate = async (templateId: string) => {
    setError(null);
    try {
      await api.delete(`/api/admin/templates/template/${templateId}`);
      // Both the admin list and the review screens' template dropdowns.
      void qc.invalidateQueries({ queryKey: ["admin-templates"] });
      void qc.invalidateQueries({ queryKey: ["templates"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the template.");
    }
  };

  const createDraft = async (basedOnTemplateId?: string) => {
    setError(null);
    try {
      const record = await api.post<TemplateRecord>("/api/admin/templates", {
        basedOnTemplateId,
        name: basedOnTemplateId ? undefined : newName,
        kind: basedOnTemplateId ? undefined : kindTab,
      });
      navigate(`/templates/${record.rowId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the draft.");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="mb-1">Templates</h2>
          <p className="max-w-2xl text-[14px] text-text-2">
            Published versions are immutable — to change a live template, create a new draft from
            it, adjust, and publish. Old versions stay so past documents remain traceable.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus size={15} strokeWidth={2} aria-hidden />
          New template
        </Button>
      </header>

      <div className="flex gap-1 self-start rounded-lg border border-border bg-surface p-1">
        {(
          [
            ["resume", "Resume templates"],
            ["jd", "Job descriptions"],
          ] as const
        ).map(([kind, label]) => (
          <button
            key={kind}
            type="button"
            onClick={() => setKindTab(kind)}
            aria-current={kindTab === kind ? "true" : undefined}
            className={cn(
              "rounded-md px-3.5 py-1.5 text-[13.5px] font-medium transition-colors",
              kindTab === kind
                ? "bg-brand-tint font-semibold text-brand-on"
                : "text-text-2 hover:bg-surface-2 hover:text-text",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <p className="rounded-md bg-danger-tint px-4 py-2.5 text-[13px] font-medium text-danger-on">
          {error}
        </p>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete "${pendingDelete?.name}"?`}
        body="Every version of this template disappears from the template list and from generation. Documents already generated with it keep working and stay traceable."
        confirmLabel="Delete template"
        danger
        onConfirm={() => {
          if (pendingDelete) void deleteTemplate(pendingDelete.templateId);
          setPendingDelete(null);
        }}
        onCancel={() => setPendingDelete(null)}
      />

      {listQuery.isLoading && (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full rounded-lg" />
          ))}
        </div>
      )}

      {visible.map(([templateId, versions]) => {
        const latest = versions[0]!;
        const draft = versions.find((v) => v.status === "draft");
        const iconMeta = templateIconFor(templateId);
        return (
          <Card key={templateId} interactive>
            <CardBody className="flex flex-wrap items-center gap-4 sm:flex-nowrap">
              <span
                className={cn(
                  "flex h-16 w-16 shrink-0 items-center justify-center rounded-full",
                  iconMeta.classes,
                )}
              >
                <iconMeta.icon size={26} strokeWidth={1.75} aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-[17px]">{latest.definition.name}</h3>
                <p className="mt-0.5 text-[13.5px] text-text-2">
                  {latest.definition.description || "No description yet."}
                </p>
                <VersionPills versions={versions} />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {draft ? (
                  <Button asChild variant="secondary" size="sm">
                    <Link to={`/templates/${draft.rowId}`}>
                      <PencilLine size={14} strokeWidth={1.75} aria-hidden /> Edit draft v
                      {draft.version}
                    </Link>
                  </Button>
                ) : (
                  <Button variant="secondary" size="sm" onClick={() => void createDraft(templateId)}>
                    <Copy size={14} strokeWidth={1.75} aria-hidden /> New draft
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-text-2 hover:text-danger-on"
                  aria-label={`Delete ${latest.definition.name}`}
                  onClick={() =>
                    setPendingDelete({ templateId, name: latest.definition.name })
                  }
                >
                  <Trash2 size={15} strokeWidth={1.75} />
                </Button>
              </div>
            </CardBody>
          </Card>
        );
      })}

      <div className="flex flex-wrap items-center gap-4 rounded-lg border-2 border-dashed border-border-2 bg-surface px-5 py-5 sm:flex-nowrap">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-surface-2 text-text-2">
          <Plus size={26} strokeWidth={1.75} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[17px]">
            {kindTab === "jd" ? "Create new JD template" : "Create new template"}
          </h3>
          {creating ? (
            <form
              className="mt-2 flex max-w-md items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (newName.trim()) void createDraft();
              }}
            >
              <Input
                ref={nameInputRef}
                value={newName}
                placeholder="Template name — e.g. Executive Search"
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button type="submit" size="sm" disabled={!newName.trim()}>
                Create
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCreating(false);
                  setNewName("");
                }}
              >
                Cancel
              </Button>
            </form>
          ) : (
            <p className="mt-0.5 text-[13.5px] text-text-2">
              Start from scratch with a blank template.
            </p>
          )}
        </div>
        {!creating && (
          <Button variant="secondary" className="shrink-0" onClick={() => setCreating(true)}>
            <Plus size={15} strokeWidth={2} aria-hidden />
            Create template
          </Button>
        )}
      </div>
    </div>
  );
}
